import { useMemo } from 'react';

import { LineChartSeries } from '@components/patterns/LineChart/types';

import { sum } from '@utils/math';

export interface ProcessedData {
  x: number;
  y: number;
  values: Record<string, number>;
}

export interface UseDataMeta {
  minIndex: number;
  maxIndex: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function useData(series: LineChartSeries<any, any>[]) {
  return useMemo(() => {
    // Calculate the min and max indices of the data
    const minIndex = 0;
    const maxIndex = Math.max(...series.map((s) => s.data.length), 1) - 1;

    // Calculate the data
    const data: ProcessedData[] = [];
    const reversedSeries = [...series].reverse();
    for (let i = minIndex; i <= maxIndex; i++) {
      const stack = reversedSeries.flatMap((s) => {
        const data = s.data.at(i);
        if (!data) return [];
        return { ...data, key: s.key };
      });
      const x = Math.max(...stack.map((v) => v.x), 0);
      const y = sum(stack.map((v) => v.y));
      data.push({
        x,
        y,
        values: Object.fromEntries(stack.map((v) => [v.key, v.y])),
      });
    }

    const keys = reversedSeries.map((s) => s.key);

    // Calculate the min and max X values of the data
    const minX = Math.min(...data.map((d) => d.x));
    let maxX = Math.max(...data.map((d) => d.x));
    maxX += (maxX - minX) / maxIndex; // Add an extra step

    // Calculate the min and max Y values of the data
    const minY = 0;
    let maxY = Math.max(...data.map((d) => d.y));
    if (minY === maxY) maxY += 1; // Ensure there is a difference between min and max

    return {
      data,
      keys,
      meta: {
        minIndex,
        maxIndex,
        minX,
        maxX,
        minY,
        maxY,
      },
    };
  }, [series]);
}
